import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import api from '../../lib/api';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';

const EMPTY_FORM = { current: '', next: '', confirm: '' };

// Change-password dialog. Owns its own form + loading state; parent only
// controls open/closed via `open` and `onOpenChange`. Form resets on close.
export function ChangePasswordDialog({ open, onOpenChange }) {
  const [cpForm, setCpForm] = useState(EMPTY_FORM);
  const [cpLoading, setCpLoading] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      onOpenChange(nextOpen);
      if (!nextOpen) setCpForm(EMPTY_FORM);
    }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }} className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#2C4C3B]" /> Change Password
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-sm">Current Password</Label>
            <Input
              type="password"
              value={cpForm.current}
              onChange={(e) => setCpForm({ ...cpForm, current: e.target.value })}
              placeholder="Current password"
              data-testid="cp-current-input"
            />
          </div>
          <div>
            <Label className="text-sm">New Password</Label>
            <Input
              type="password"
              value={cpForm.next}
              onChange={(e) => setCpForm({ ...cpForm, next: e.target.value })}
              placeholder="At least 6 characters"
              data-testid="cp-new-input"
            />
          </div>
          <div>
            <Label className="text-sm">Confirm New Password</Label>
            <Input
              type="password"
              value={cpForm.confirm}
              onChange={(e) => setCpForm({ ...cpForm, confirm: e.target.value })}
              placeholder="Repeat new password"
              data-testid="cp-confirm-input"
            />
          </div>
          <Button
            onClick={async () => {
              if (!cpForm.current || !cpForm.next) { toast.error('All fields required'); return; }
              if (cpForm.next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
              if (cpForm.next !== cpForm.confirm) { toast.error('Passwords do not match'); return; }
              setCpLoading(true);
              try {
                await api.post('/auth/change-password', {
                  current_password: cpForm.current,
                  new_password: cpForm.next,
                });
                toast.success('Password changed successfully');
                onOpenChange(false);
                setCpForm(EMPTY_FORM);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to change password');
              }
              setCpLoading(false);
            }}
            disabled={cpLoading}
            className="w-full bg-[#2C4C3B] hover:bg-[#3D6A53] text-white"
            data-testid="cp-submit-btn"
          >
            {cpLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Change Password
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
